import {
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import {Page} from '../../../model/page';
import 'rxjs/add/observable/of';
import 'rxjs/add/operator/map';
import 'rxjs/add/operator/do';
import 'rxjs/add/operator/delay';

import {
  ActivatedRoute,
  ActivatedRouteSnapshot,
  Router,
} from '@angular/router';
import {permissions} from '../../../permissions';
import {MasterDataService} from '../services/master-data.service';
import {AuthService} from '../../../auth/auth.service';
import {exportCols, newOrderTableCols, altExportCols, altNewOrderTableCols} from './master-data.cols';
import {ToastrService} from 'ngx-toastr';
import {IMasterData} from '../interfaces/IMasterData';
import {IMasterDataList} from '../interfaces/IMasterDataList';
import {MatDialog} from '@angular/material/dialog';
import {ConfirmationService} from 'primeng/api';
import {Title} from '@angular/platform-browser';
import {Subscription} from 'rxjs/Subscription';
import {ImportMasterDataModalComponent} from '../../../components/import-master-data-modal/import-master-data-modal.component';
import {ExportMasterDataModalComponent} from '../../../components/export-master-data-modal/export-master-data-modal.component';
import {SortService} from '../services/sort.service';
import {CurrencyPipe} from '@angular/common';
import {TableExtensionService} from '../table-extension.service';
import {altOldTableCols, altTableCols, tableCols} from '../scanned-data/scanned-data.cols';
import {fiveDecCompanies} from '../../../../environments/environment';

@Component({
  templateUrl: './master-data.component.html',
  providers: [
    MasterDataService,
    AuthService,
    SortService,
    TableExtensionService,
  ],
  styleUrls: ['./master-data.component.scss'],
})
export class MasterDataComponent implements OnInit, OnDestroy {
  @ViewChild('masterDataView') masterDataView: ElementRef;
  @ViewChild('actionbar') actionbar: ElementRef;
  @ViewChild('content') content: ElementRef;
  @ViewChild('tableHeader') tableHeader: ElementRef;
  @ViewChild('tableFilter') tableFilter: ElementRef;
  @ViewChild('paginator') paginator: ElementRef;

  public page = new Page();
  rows: Array<IMasterDataList> = [];
  tooltipRows: Array<any> = [];
  cols = [];
  exportCols = [];
  totalRecords = 0;
  size = 50;
  hideTooltip = false;

  private subscription: Subscription;
  private exportSubscription: Subscription;
  private importSubscription: Subscription;

  private selected: any = null;
  private viewFilters = false;
  private loading = false;
  public userActionLoading = false;
  public isWorkspaceSelected: boolean;
  public filtersApplied = false;
  public sortApplied: boolean;
  public sortField: string;
  public sortOrder: number;

  public scrollHeight = '50px';
  private viewportHeight = 0;
  private viewportHeightListener: boolean;

  private yesNoFlags: Array<object> = [
    {value: null, text: 'All'},
    {value: '1', text: 'Yes'},
    {value: '0', text: 'No'},
  ];

  private permissions = permissions;

  constructor(
    private masterDataService: MasterDataService,
    private authService: AuthService,
    private toast: ToastrService,
    private sortService: SortService,
    private router: Router,
    private route: ActivatedRoute,
    private dialog: MatDialog,
    private confirmationService: ConfirmationService,
    private title: Title,
    private currencyPipe: CurrencyPipe,
    private tableExtensionService: TableExtensionService
  ) {
    this.page.pageNumber = 0;
    this.page.size = 50;
    this.title.setTitle('OES - Master Data');
  }

  ngOnInit() {
    this.isWorkspaceSelected = this.getSelectedWorkspace();

    if (this.authService.permissions.includes('VIEW_PRICING_INFO')) {
      this.cols = newOrderTableCols;
      this.exportCols = exportCols;
    } else {
      this.cols = altNewOrderTableCols;
      this.exportCols = altExportCols;
    }

    this.sortApplied = false;
    this.sortField = '';
    this.sortOrder = 0;

    this.viewportHeightListener = true;

    const filterParams = this.route.snapshot.queryParams;
    this.parseParams(filterParams);

    if (this.isWorkspaceSelected) {
      this.getData(false);
      this.screenHeightListener();
    }
  }

  ngOnDestroy() {
    this.viewportHeightListener = false;
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    if (this.exportSubscription) {
      this.exportSubscription.unsubscribe();
    }
    if (this.importSubscription) {
      this.importSubscription.unsubscribe();
    }
  }

  parseParams(filterParams) {
    if (filterParams !== undefined && filterParams !== null) {
      let showFilters = false;
      let sortApplied = false;
      for (const key of Object.keys(filterParams)) {
        const value = filterParams[key];
        if (value && value.toString() !== '') {
          if (key.toString() === 'page') {
            this.page.pageNumber = Number(value);
          } else if (key.toString() === 'perPage') {
            this.page.size = Number(value);
            this.size = Number(value);
          } else if (
              key.toString() === 'SORT_COLUMN' ||
              key.toString() === 'SORT_DIRECTION'
          ) {
            this.page.filterParams[key] = value;
            sortApplied = true;
          } else {
            this.page.filterParams[key] = value;
            showFilters = true;
          }
        }
      }
      this.viewFilters = showFilters;
      this.filtersApplied = showFilters;
      this.sortApplied = sortApplied;

      this.parseSort(filterParams);
    }
  }

  private parseSort(filterParams) {
    const sortDetails = this.sortService.parseSort(filterParams);
    this.sortField = sortDetails.sortField;
    this.sortOrder = sortDetails.sortOrder;
  }

  onHover(e) {
    const aux = e.target.offsetWidth < e.target.scrollWidth;
    this.hideTooltip = !aux;
  }

  getData(reloadWithParams = true) {
    this.setPage({ offset: this.page.pageNumber }, reloadWithParams);
  }

  setPage(pageInfo, reloadWithParams = true) {
    if (reloadWithParams) {
      this.page.filterParams['page'] = pageInfo.offset ? pageInfo.offset : 0;
      this.page.filterParams['perPage'] = pageInfo.size
          ? pageInfo.size
          : this.size;
      this.router.routeReuseStrategy.shouldReuseRoute = function (
          future: ActivatedRouteSnapshot,
          curr: ActivatedRouteSnapshot
      ) {
        return false;
      };
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: this.page.filterParams,
      });
    } else {
      this.loading = true;
      this.selected = null;
      this.subscription = this.masterDataService
        .getDataList(this.page)
        .subscribe(
          (result) => {
            this.totalRecords = result.count;
            this.rows = result.data.map((masterData) => {
              masterData['MD1_ON_HAND_COLOR'] = '#ffffff';
              masterData['MD1_ON_HAND_BG'] = '#909090';
              if (
                  Number(masterData['MD1_MIN_QTY']) <=
                  Number(masterData['MD1_MAX_QTY'])
              ) {
                if (
                    Number(masterData['MD1_ON_HAND_QTY']) <
                    Number(masterData['MD1_MIN_QTY'])
                ) {
                  masterData['MD1_ON_HAND_BG'] = '#ff0000';
                } else if (
                    Number(masterData['MD1_ON_HAND_QTY']) ===
                    Number(masterData['MD1_MAX_QTY'])
                ) {
                  masterData['MD1_ON_HAND_BG'] = '#34a330';
                } else if (
                    Number(masterData['MD1_ON_HAND_QTY']) <
                    Number(masterData['MD1_MAX_QTY']) &&
                    Number(masterData['MD1_ON_HAND_QTY']) >=
                    Number(masterData['MD1_MIN_QTY'])
                ) {
                  masterData['MD1_ON_HAND_BG'] = '#1959e1';
                } else if (
                    Number(masterData['MD1_ON_HAND_QTY']) >
                    Number(masterData['MD1_MAX_QTY'])
                ) {
                  masterData['MD1_ON_HAND_BG'] = '#fdff00';
                }
              }
              return masterData;
            });
            this.tooltipRows = this.getTooltipTextArray();
            const cu0InventoryManagementEnabled =
              Number(result.masterCustomer.CU0_ALLOW_INVENTORY_MANAGEMENT) ===
              1;
            const onHandColIndex = this.cols.findIndex(
                (col) => col.field === 'MD1_ON_HAND_QTY'
            );
            if (onHandColIndex > -1) {
              this.cols[onHandColIndex].visible = cu0InventoryManagementEnabled;
              this.cols[onHandColIndex].filter = cu0InventoryManagementEnabled;
              this.cols[onHandColIndex].export.visible =
                cu0InventoryManagementEnabled;
              this.cols[onHandColIndex].export.checked =
                cu0InventoryManagementEnabled;
            }
            setTimeout(() => {
              this.scrollHeight = this.getScrollHeight() + 'px';
            });
            this.loading = false;
          },
          () => {
            this.loading = false;
            window.dispatchEvent(new Event('resize'));
            this.toast.error('Request timed out.', 'Service Error', {
              timeOut: 3000,
            });
          }
        );
    }
  }

  buttonPress(e, col, value) {
    if (e.keyCode === 13) {
      this.filterChanged(col, value);
    }
  }

  clearFilters() {
    this.page.filterParams = {};
    this.viewFilters = false;
    this.filtersApplied = false;
    this.page.pageNumber = 0;
    this.setPage({ offset: this.page.pageNumber });
  }

  filterChanged(col, value) {
    this.loading = true;

    if (col === 'MD1_SHOW_DEFAULT') {
      this.page.filterParams[col] = value;
    } else if (
        ((col === 'ORDERED_ON' ||
                col === 'COMPLETED_ON' ||
                col === 'MODIFIED_ON' ||
                col === 'CREATED_ON') &&
            value === '01-01-1970') ||
        value === ''
    ) {
      delete this.page.filterParams[col];
    } else {
      this.page.filterParams[col] = value;
    }
    this.filtersApplied = Object.keys(this.page.filterParams).length > 0;
    this.page.pageNumber = 0;
    this.setPage({ offset: this.page.pageNumber });
  }

  getContentDialogHeight(): number {
    return (
      window.innerHeight - (this.actionbar.nativeElement.offsetHeight + 48) - 77
    ); // 77 for header & footer
    // return (this.masterDataView.nativeElement.parentElement.parentElement.parentElement.offsetHeight) -
    //     (this.actionbar.nativeElement.offsetHeight + 48);
  }

  getScrollHeight(): number {
    return (
      this.getContentDialogHeight() -
      (this.tableHeader.nativeElement.offsetHeight +
        (this.paginator.nativeElement.offsetHeight + 55) +
        (this.viewFilters && this.tableFilter
          ? this.tableFilter.nativeElement.offsetHeight
          : 0))
    );
  }

  getSelectedWorkspace() {
    return this.authService.workspaceMasterCustomerID !== null;
  }

  get masterDataViewHeight() {
    return this.masterDataView.nativeElement.parentElement.parentElement
      .parentElement.offsetHeight;
  }

  get masterActionBarHeight() {
    return this.actionbar.nativeElement.offsetHeight + 48;
  }

  exportClick() {
    const dialogRef = this.dialog.open(ExportMasterDataModalComponent, {
      width: window.innerWidth - 100 + 'px',
      height: window.innerHeight - 80 + 'px',
      role: 'dialog',
      data: {cols: this.cols},
    });

    dialogRef.beforeClose().subscribe((preferences) => {
      if (preferences) {
        this.exportSubscription = this.masterDataService
          .export(preferences, this.page)
          .subscribe(
            (result) => {
              if (result.success) {
                this.toast.info(
                    'Export is added to queue.' +
                    ' Please check the File Exports to download the file once it is ready!',
                    'Update'
                );
              } else {
                this.toast.error('Error in exporting data.', 'Error', {
                  timeOut: 3000,
                  tapToDismiss: true,
                });
              }
            },
            () => {
              this.toast.error('Request timed out.', 'Service Error', {
                timeOut: 3000,
                tapToDismiss: true,
              });
            }
          );
      }
    });
  }

  importClick() {
    const dialogRef = this.dialog.open(ImportMasterDataModalComponent, {
      width: window.innerWidth - 100 + 'px',
      height: window.innerHeight - 80 + 'px',
      role: 'dialog',
      data: {},
    });

    dialogRef.beforeClose().subscribe((importContent) => {
      if (importContent !== null) {
        const customerSplit = importContent.customer.split('_');
        this.importSubscription = this.masterDataService
          .importFromFile(
            importContent.file,
            customerSplit[0],
            customerSplit[1]
          )
          .subscribe(
            (result) => {
              if (result.success) {
                this.toast.info('Master data import queued.', 'Update', {
                  timeOut: 3000,
                  tapToDismiss: true,
                });
              } else {
                this.toast.error(
                    'Error encountered adding file to import queue.',
                    'Error',
                    {timeOut: 3000, tapToDismiss: true}
                );
              }
            },
            () => {
              this.toast.error('Error in importing data.', 'Service Error', {
                timeOut: 3000,
                tapToDismiss: true,
              });
            }
          );
      }
    });
  }

  viewImportQueue() {
    this.router.navigate(['order-entry-system/master-data/import-queue']);
  }

  onPage(event) {
    this.setPage({
      offset: event.first / event.rows,
      size: event.rows,
    });
  }

  onColumnResize(event) {
    this.tableExtensionService.onColumnResize(this.cols, event);
  }

  onSort(col) {
    if (col.sort) {
      if (this.page.filterParams.hasOwnProperty('SORT_COLUMN')) {
        if (this.page.filterParams['SORT_COLUMN'] === col.field) {
          if (this.page.filterParams['SORT_DIRECTION'] === 'ASC') {
            this.page.filterParams['SORT_DIRECTION'] = 'DESC';
          } else {
            delete this.page.filterParams['SORT_DIRECTION'];
            delete this.page.filterParams['SORT_COLUMN'];
          }
        } else {
          this.page.filterParams['SORT_COLUMN'] = col.field;
          this.page.filterParams['SORT_DIRECTION'] = 'ASC';
        }
      } else {
        this.page.filterParams['SORT_COLUMN'] = col.field;
        this.page.filterParams['SORT_DIRECTION'] = 'ASC';
      }

      this.parseSort(this.page.filterParams);
      this.getData();
    }
  }

  onView(data: IMasterData) {
    this.router.navigate([
      'order-entry-system/master-data/view/' + data.MD1_ID,
    ]);
  }

  editClick() {
    if (this.selected) {
      this.router.navigate([
        'order-entry-system/master-data/edit/' + this.selected['MD1_ID'],
      ]);
    }
  }

  newClick() {
    this.router.navigate(['order-entry-system/master-data/add']);
  }

  deleteClick() {
    if (this.selected) {
      this.confirmationService.confirm({
        message:
            'Are you sure to delete Bin ID - ' +
            this.selected.CONTRACT_BIN_ID +
            '?',
        header: 'Delete Record',
        accept: () => {
          this.userActionLoading = true;
          this.masterDataService.delete(this.selected).subscribe(
              (result) => {
                if (result.success) {
                  this.toast.info(
                      'Master data delete request successful.',
                      'Request Complete'
                  );
                  this.userActionLoading = false;
                  if (this.rows.length === 1) {
                    this.clearFilters();
                  } else {
                    this.getData(false);
                  }
                } else {
                  this.toast.error(
                      'Unable to delete the record. Please try later.',
                      'Error'
                  );
                this.userActionLoading = false;
              }
            },
            () => {
              this.toast.error('Request timed out.', 'Service Error', {
                timeOut: 3000,
                tapToDismiss: true,
              });
              this.userActionLoading = false;
            }
          );
        },
      });
    }
  }

  refreshClick() {
    this.getData(false);
  }

  toggleFilterView() {
    this.viewFilters = !this.viewFilters;
    this.onWindowResize();
  }

  onWindowResize() {
    setTimeout(() => {
      this.scrollHeight = this.getScrollHeight() + 'px';
      window.dispatchEvent(new Event('resize'));
    }, 150);
  }

  screenHeightListener() {
    if (!this.viewportHeightListener) {
      return;
    }
    if (window.innerHeight !== this.viewportHeight) {
      this.viewportHeight = window.innerHeight;
      this.onWindowResize();
    }
    setTimeout(() => {
      this.screenHeightListener();
    }, 500);
  }

  getTooltipTextArray() {
    this.rows.forEach((element) => {
      const newObj = JSON.parse(JSON.stringify(element)); // Object.assign({}, element);

      newObj['MD1_PRICE'] = this.currencyPipe.transform(
          newObj['MD1_PRICE'],
          'USD',
          'symbol'
      );

      this.tooltipRows.push(newObj);
    }, this);
    return this.tooltipRows;
  }

  protected readonly fiveDecCompanies = fiveDecCompanies;
  protected readonly parseInt = parseInt;
}



// WEBPACK FOOTER //
// /home/ubuntu/edi/angular/app/views/oes/master-data/master-data.component.ts